import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Text,
  Paper,
  Image,
  SimpleGrid,
  Stack,
  ThemeIcon,
  Card,
} from '@mantine/core';
import { Loader } from '../../components/common/Loader';
import { Helmet } from 'react-helmet-async';
import { IconShield, IconRipple, IconPackages, IconMicroscope } from '@tabler/icons-react';
import { useAppSelector } from '../../store/hooks';
import { SectionTitle } from '../../components/common/SectionTitle';
import { useSEO } from '../../hooks/useSEO';
import { galleryService } from '../../services/galleryService';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';
const PLACEHOLDER_IMG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999'%3ENo Image%3C/text%3E%3C/svg%3E";

const qualityIcons: Record<string, any> = {
  gmp: IconShield,
  haccp: IconRipple,
  clean: IconPackages,
  hygiene: IconMicroscope,
};

export const QualityPage: React.FC = () => {
  const { content } = useAppSelector((state) => state.content);
  const staticQuality = content.pages.quality;
  const { seo, loading: seoLoading } = useSEO('quality');
  
  const [processSteps, setProcessSteps] = useState(staticQuality.qualityProcess);
  const [packagingTypes, setPackagingTypes] = useState(staticQuality.packagingTypes);
  const [loading, setLoading] = useState(true);
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    const fetchDynamicData = async () => {
      try {
        const [processGallery, packagingGallery] = await Promise.all([
          galleryService.getPublicGallery('QUALITY_PROCESS'),
          galleryService.getPublicGallery('PACKAGING')
        ]);

        // Process steps
        const processItems = processGallery.items
          .filter(item => item.type === 'IMAGE' && item.active)
          .map(item => {
            let meta = {};
            try { meta = JSON.parse(item.metadata || '{}'); } catch(e) {}
            return {
              title: item.title,
              description: item.description,
              image: item.imageUrl,
              order: (meta as any).order || 0,
            };
          })
          .sort((a, b) => a.order - b.order);

        // Packaging types
        const packagingItems = packagingGallery.items
          .filter(item => item.type === 'IMAGE' && item.active)
          .map(item => {
            let meta = {};
            try { meta = JSON.parse(item.metadata || '{}'); } catch(e) {}
            return {
              name: item.title,
              description: item.description,
              specifications: (meta as any).specifications || '',
              image: item.imageUrl,
              order: (meta as any).order || 0,
            };
          })
          .sort((a, b) => a.order - b.order);

        if (processItems.length > 0) setProcessSteps(processItems);
        if (packagingItems.length > 0) setPackagingTypes(packagingItems);
        setUseFallback(processItems.length === 0 && packagingItems.length === 0);
      } catch (err) {
        console.error('Failed to fetch quality data:', err);
        setUseFallback(true);
      } finally {
        setLoading(false);
      }
    };
    fetchDynamicData();
  }, []);

  const getImageUrl = (url: string) => {
    if (!url) return PLACEHOLDER_IMG;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/assets')) return url;
    return `${API_URL}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  if (seoLoading || loading) {
    return (
      <Container className="flex justify-center items-center h-64">
        <Loader />
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{seo?.metaTitle}</title>
        <meta name="description" content={seo?.metaDescription} />
        <meta name="keywords" content={seo?.metaKeywords?.join(', ')} />
        <meta property="og:title" content={seo?.ogTitle} />
        <meta property="og:description" content={seo?.ogDescription} />
        {seo?.ogImage && <meta property="og:image" content={seo.ogImage} />}
        <meta name="twitter:title" content={seo?.twitterTitle} />
        <meta name="twitter:description" content={seo?.twitterDescription} />
        {seo?.twitterImage && <meta name="twitter:image" content={seo.twitterImage} />}
        {seo?.canonicalUrl && <link rel="canonical" href={seo.canonicalUrl} />}
      </Helmet>

      <Container size="xl">
        <SectionTitle title={staticQuality.title} subtitle={staticQuality.description} />

        {/* Quality Process */}
        <Grid gutter={30} mb={50}>
          {processSteps.map((step, index) => (
            <Grid.Col key={index} span={{ base: 12, sm: 6, md: 3 }}>
              <Card shadow="sm" padding="lg" radius="lg" withBorder>
                <Card.Section>
                  <Image
                    src={getImageUrl(step.image)}
                    height={200}
                    alt={step.title}
                    fallbackSrc={PLACEHOLDER_IMG}
                    loading="lazy" 
                  />
                </Card.Section>
                <Text fw={600} size="lg" mt="md">{step.title}</Text>
                <Text size="sm" c="dimmed" mt="xs">{step.description}</Text>
              </Card>
            </Grid.Col>
          ))}
        </Grid>

        {/* Hygiene Standards (still static) */}
        <SectionTitle title="Hygiene Standards" />
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="lg" mb={50}>
          {staticQuality.hygieneStandards.map((standard) => {
            const IconComponent = qualityIcons[standard.icon] || IconShield;
            return (
              <Paper key={standard.title} withBorder p="xl" radius="lg">
                <Stack align="center" gap="md">
                  <ThemeIcon size={60} radius="xl" color="primary" variant="light">
                    <IconComponent size={30} />
                  </ThemeIcon>
                  <Text fw={600} size="lg" ta="center">{standard.title}</Text>
                  <Text ta="center" size="sm" c="dimmed">{standard.description}</Text>
                </Stack>
              </Paper>
            );
          })}
        </SimpleGrid>

        {/* Packaging Types */}
        <SectionTitle title="Packaging Types" />
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
          {packagingTypes.map((pack) => (
            <Card key={pack.name} shadow="sm" padding="lg" radius="lg" withBorder>
              <Card.Section>
                <Image
                  src={getImageUrl(pack.image)}
                  height={200}
                  alt={pack.name}
                  fallbackSrc={PLACEHOLDER_IMG}
                />
              </Card.Section>
              <Text fw={600} size="lg" mt="md">{pack.name}</Text>
              <Text size="sm" c="dimmed" mt="xs">{pack.description}</Text>
              <Text size="sm" fw={500} mt="md">Specifications:</Text>
              <Text size="sm" c="dimmed">{pack.specifications}</Text>
            </Card>
          ))}
        </SimpleGrid>
      </Container>
    </>
  );
};